import type { EventId, FigureId, TilePos } from "../world/types";
import type { Interactable, InteractSubject } from "./interact";

/*
 * What the town's law makes of a press, on this side of the wall.
 *
 * Chunk Fx-2, out of the wave-2 playtest: the owner stole in front of a witness,
 * heard the town knew, stood next to the guard and nothing happened — and, a
 * moment earlier, was offered *dig* while pointing at a neighbour. The two
 * findings are one subject. This file is the rules half of it, sitting beside
 * `witness/witness.ts` the way `arms.ts` and `company.ts` sit beside it for
 * their verbs (`docs/decisions/witness.md`, chunk 2G: a verb's rules go to a
 * sibling, and the file that keeps them stays readable).
 *
 * Four rules and two pieces of copy, and every one of them is a *shape* over
 * answers the simulation gave:
 *
 * - **who needs to know where the player is standing** ({@link needsMyTile}) —
 *   the stand stream's trigger, which is the client half of the arrest deadlock:
 *   the simulation engages only against a tile it has been told, and the client
 *   used to tell it only once somebody was already engaged;
 * - **what the left mouse button does** ({@link mouseAnswer}) — three answers
 *   where there were two, because the owner's *"why do I get mouse dig when
 *   hovering an npc"* is answered by offering nothing rather than by digging
 *   through a person;
 * - **how near a watchman has to be** ({@link ACCUSATION_TILES}) — one number,
 *   shared by the two surfaces that read it;
 * - **which press the crime read is even about**
 *   ({@link tileATakeWouldCite}) — two subjects out of twelve, because two of
 *   them are `Command::Take` and the rest are nobody's business;
 * - **what a hand on your shoulder leaves you** ({@link arrestBeat}) — the three
 *   answers an arrest has, named and never asked for;
 * - **how that hand comes off again** ({@link reprimandClosed},
 *   {@link reprimandLine}, chunk Fx-7) — the row closing with a word instead of
 *   a taking, which is the one sentence in an arrest nothing was saying;
 * - **which of two things on one tile a press is about**
 *   ({@link bodyBeforeHeap}) — `take`'s own order, so the noun and the crime
 *   word are about the same subject;
 * - **the crime word on a prompt** ({@link crimeTag}) — brackets around a noun
 *   the *simulation* chose, never a table kept here.
 *
 * Nothing in this file asks the world anything. Every input is an answer a
 * caller already has, which is what makes the rules checkable without a
 * simulation behind them (`dev/selfcheck/telling.ts`).
 */

/**
 * How near a watchman has to be before he says anything to the player, in
 * tiles: 6.
 *
 * **PROVISIONAL**, and deliberately *narrower* than the staged reactions'
 * earshot (`witness/staging.ts`'s nine, which is somebody speaking to a street):
 * an accusation is addressed to one person, so it carries about as far as the
 * interact range does and no further.
 *
 * The simulation's own reach for actually taking somebody is one tile
 * (`sim_core`'s `watch`), and this is wider on purpose: a guard who has walked
 * to the scene of a theft the player committed has nobody to put a hand on —
 * the player has no body — so what is left is that he is *here*, and six tiles
 * is standing over you.
 *
 * **It has two readers since chunk Fx-2 and it is one constant on purpose.** The
 * accusation is said at this distance and the player's tile starts crossing at
 * this distance ({@link needsMyTile}), because they are the same moment seen
 * from two sides: the guard is close enough to speak, and the simulation is
 * about to need somewhere to send him. Two numbers here would be a guard who
 * says the line and then cannot reach, or reaches without ever having said it.
 */
export const ACCUSATION_TILES = 6;

/** One body as this file needs it: who, and the tile the simulation has them on. */
export interface StandingBody {
  readonly figure: FigureId;
  readonly at: TilePos;
}

/** A body and how far away it is, in the town layer's own distance. */
export interface BodyNear {
  readonly figure: FigureId;
  readonly away: number;
}

/**
 * How far apart two tiles are, the town layer's way: the long side, not the
 * diagonal.
 *
 * Chebyshev, which is the distance `sim_core`'s `watch` measures an arrest and a
 * witness in, and the level is deliberately not in it — a guard one floor up
 * from a thief is standing over him as far as this client is concerned, exactly
 * as he is as far as the simulation is.
 */
export function tilesApart(from: TilePos, to: TilePos): number {
  return Math.max(Math.abs(from.x - to.x), Math.abs(from.y - to.y));
}

/**
 * The nearest of a list of bodies within {@link ACCUSATION_TILES}, or `null`.
 *
 * Chebyshev, which is the distance every other question about a town tile is
 * asked in (`sim_core`'s `watch` measures the same way, and a diagonal step is
 * one step in both). Ties go to the lower handle, so two guards equidistant from
 * a thief produce the same answer on every frame rather than trading the line
 * between them.
 */
export function nearestWithin(
  from: TilePos,
  bodies: readonly StandingBody[],
): BodyNear | null {
  let best: BodyNear | null = null;

  for (const body of bodies) {
    const away = tilesApart(from, body.at);

    if (away > ACCUSATION_TILES) {
      continue;
    }

    if (best === null || away < best.away || (away === best.away && body.figure < best.figure)) {
      best = { figure: body.figure, away };
    }
  }

  return best;
}

/**
 * Whether anybody in the promoted town needs to know where the player is
 * standing.
 *
 * **Three clauses, and the third is chunk Fx-2's** (W11's rule, as wave 2F
 * widened it and the arrest probe widened it again):
 *
 * - **a wind-up** needs it because a blow is measured from it;
 * - **a follower** needs it because a companion walks after the tile the host
 *   states and the player has no body to walk after;
 * - **a wanted player with a watchman beside them** needs it because that is the
 *   moment before an arrest, and without it the arrest cannot happen at all.
 *
 * The third clause is the client half of a deadlock the live probe pinned
 * (`docs/decisions/watch.md`, chunk Fx-1): the simulation engages a culprit
 * against the tile it has been told, and this client only ever told it once
 * somebody was already engaged — so a guard walked to the scene of the theft,
 * stood there, and neither side could move first. The two reads it costs are the
 * two the accusation line already makes, which is why it is this clause and not
 * a wider one: *wanted*, and *a watchman within earshot*.
 *
 * **The bargain the rule was written under survives it.** A lone walk still
 * costs the command log nothing; what a replay carries is every fight, every
 * accompanied walk, and every walk taken while the town wanted you and had
 * somebody standing next to you — which is a sentence the log is *for*.
 */
export function needsMyTile(
  threatened: boolean,
  followers: number,
  wanted: boolean,
  watchNear: boolean,
): boolean {
  return threatened || followers > 0 || (wanted && watchNear);
}

/** What the left mouse button would do right now. */
export type MouseAnswer = "strike" | "dig" | "nothing";

/**
 * The mouse-0 disambiguation rule, and it has three answers since chunk Fx-2.
 *
 * **The owner's ruling of 2026-08-31 (option 2)**: *"why do I get mouse dig when
 * hovering an npc? strange choice … you have no quarrel."* The quarrel gate
 * itself stands — a bare-handed player throws no first punch, which is the
 * asymmetry wave 2D-a argued and the owner accepted — and what was wrong was the
 * *offer*: falling through to a dig at the voxel behind a person reads as
 * digging through them.
 *
 * So the three answers, in the order they are asked:
 *
 * - **not at a body, or at one the simulation would not let a blow cross** →
 *   `dig`, exactly as it has meant since Phase 1. This is the disambiguation
 *   case the ruling had to mind: a player aiming *past* somebody at the ground
 *   beyond them is a player digging, and a body four tiles away is nobody the
 *   press could have hit;
 * - **at a body in reach, with a quarrel** → `strike`;
 * - **at a body in reach, with no quarrel** → `nothing`, and the prompt says
 *   why. Nothing is offered, so nothing is dug through.
 *
 * `inReach` is the *simulation's* predicate over the body's tile
 * (`world/types.ts`'s `withinStrikeReach`) rather than the client's aiming
 * range, which is what makes the suppression exactly as wide as the swing that
 * is being refused: one tile further out and the answer is a dig again.
 */
export function mouseAnswer(atABody: boolean, inReach: boolean, quarrel: boolean): MouseAnswer {
  if (!atABody || !inReach) {
    return "dig";
  }

  return quarrel ? "strike" : "nothing";
}

/**
 * What a player does about a hand on their shoulder, named rather than asked
 * for.
 *
 * **The arrest beat, and it asks for nothing** (wave 2D-a's ruling, `player.md`
 * §6): an arrest is a moment with exactly three answers and every one of them is
 * already a control the player has — stand still, swing, or walk off — so the
 * line *names* them and invents no gesture for the occasion. Which of the three
 * the simulation reads is not decided here either: it reads the blow on the
 * watchman's own body if there is one, and silence otherwise
 * (`sim_core::watch`'s `submits`).
 *
 * **It stopped depending on where the crosshair is pointing in chunk Fx-2**, and
 * the live proof is what found it: the beat used to be a clause on the swing
 * line, appended only while the aimed body *was* the arrester — so a guard with
 * a neighbour standing a little nearer, or a player looking at the sky, was a
 * player being arrested with nothing on the screen about it. The three answers
 * are true wherever the player is looking, so the line is drawn wherever they
 * are looking.
 *
 * The resist clause is an instruction rather than a claim about the button
 * (*"swing at them"*, not *"the mouse will"*), because a swing needs the
 * crosshair on him and this line does not: the prompt must never say the press
 * will do something it would not.
 *
 * `who` is empty for somebody this town's roster has no name for, and the line
 * says *the watch* — never a handle, which is the debug window's spelling and
 * not a player's.
 */
export function arrestBeat(who: string): string {
  return (
    `${who === "" ? "the watch" : who} has hold of you — stand still and give yourself up` +
    ` · swing at them and resist · walk away and stay wanted`
  );
}

/**
 * The row the watch had somebody in hand for, as this client watched it: what
 * the town wanted them for, whose hand it was, and what the log said about them
 * at that moment.
 *
 * Remembered rather than asked for afterwards, because **the reprimand is the
 * difference between two frames** and there is nothing left to read once it has
 * happened: the sentence below a taking is recordless by design
 * (`sim_core::watch`), so the row simply is not there any more.
 */
export interface RowInHand {
  readonly crime: string;
  readonly by: FigureId;

  /**
   * The last taking on the player's name when the hand landed, or `null`.
   *
   * The one thing that tells a reprimand from a custody, and it is the same
   * derivation the waking line makes one crossing over (`host/rest.ts`'s
   * `townsAnswer`): a record that was not there before is what makes a taking
   * *news*, and its absence is what makes this a word and let go.
   */
  readonly taken: EventId | null;
}

/**
 * Whether the row this hand was on closed with a word rather than with a
 * taking.
 *
 * Two clauses, and both are absences — which is exactly what a reprimand is
 * (`sim_core`'s `Sentence::Reprimand`: *a word, and let go*, and nothing
 * written down):
 *
 * - **nobody is holding them any more.** The engagement is what an arrest *is*
 *   while it lasts, and the watchman goes back to his beat the pulse after
 *   `carry_out` settles the row;
 * - **and the log says no more about them than it did.** A taking writes
 *   `Arrested` and the waking line quotes it; a reprimand writes nothing, so a
 *   client that did not check this would put a word in the guard's mouth on the
 *   way to the cells.
 *
 * The third clause — that the town no longer wants them — is the caller's, and
 * it is there rather than here because it is what makes this worth asking at
 * all ({@link RowInHand} is only remembered while the row is open).
 */
export function reprimandClosed(
  held: RowInHand,
  hand: FigureId | null,
  taken: EventId | null,
): boolean {
  return hand === null && taken === held.taken;
}

/**
 * What the guard says as he lets the player go.
 *
 * **The arrest beat's closure** (chunk Fx-7, off the owner's session of
 * 2026-09-01: *"a guard comes over … and then nothing happens? The guard just
 * left."*). Nothing was broken — that was `Sentence::Reprimand` working as
 * written, a word and let go over about two seconds — and the defect was that
 * the *word* was the one thing never said: the beat's three answers were on the
 * screen for the length of the engagement and then the man walked off.
 *
 * Composed on this side of the wall, which is the same licence
 * `hud/crossing.ts`'s `settledWithYou` takes and for the same reason: there is
 * no record to quote, because everything below a taking is seen and never logged
 * (`sim_core::watch`). Said in the accusation's own register
 * (`witness/witness.ts`'s `answerForIt`: *"The watch wants a word with you about
 * that theft"*) with the crime word still the simulation's — this client has
 * never owned a crime table.
 *
 * **What it threatens is the sentence one rung up, in the simulation's own
 * words** (`sim_core`'s `Sentence::Custody`: *"and was taken by the watch"*).
 * The plan's own sketch of this line said *next time it is the cells*, and the
 * cells are a building this world has no data for — a client that promised one
 * would be inventing an institution to make a sentence land.
 */
export function reprimandLine(crime: string): string {
  return `That was your one word about the ${crime} — next time the watch takes you.`;
}

/**
 * Which of the things standing on one tile a press is actually about: the body,
 * where there is one to lift.
 *
 * **`Command::Take`'s own order, applied to the noun** (chunk Fx-7, closing
 * chunk Fx-2's filing). The crime read follows the simulation's pick — a body
 * before an item, both in handle order (`sim_core::hands`) — while the *prompt*
 * named whichever subject the crosshair happened to be nearest to, so a player
 * aiming at a boulder with a baby lying on it read `pick up a boulder
 * (abduction)`: the right word about the wrong thing.
 *
 * The pick itself is not touched. This is applied to its answer, and only where
 * that answer is a heap with a portable body standing on its tile — which is
 * the one case the two can disagree about, because everything else a press
 * addresses is not a `Take` at all ({@link tileATakeWouldCite}).
 *
 * It is the same argument chunk B's list order makes one collision over — *a
 * thing standing on a tile is addressed before the tile's own standing verb* —
 * and it has to be a rule here rather than an ordering because a body glides
 * between two sim reads (`witness/scenes.ts`) and a heap does not, so which of
 * the two is a hair nearer is a matter of what frame it is.
 */
export function bodyBeforeHeap(
  picked: Interactable | null,
  candidates: readonly Interactable[],
): Interactable | null {
  if (picked === null || picked.subject.kind !== "item") {
    return picked;
  }

  const heap = picked.subject.at;

  return (
    candidates.find(
      (entry) =>
        entry.subject.kind === "npc" &&
        entry.subject.carry === "take" &&
        entry.subject.at !== null &&
        entry.subject.at.x === heap.x &&
        entry.subject.at.y === heap.y &&
        entry.subject.at.z === heap.z,
    ) ?? picked
  );
}

/**
 * The tile `F` on a subject would take something off, or `null` for a press that
 * takes nothing.
 *
 * **Two subjects out of twelve**, which is the honest edge of what the crime
 * read can answer: a pickup off a heap and a body lifted off a doorstep both go
 * through `Command::Take`, and the simulation answers about the *tile* that
 * command cites (`Snapshot::crime_of_taking`). Reading a grave, talking to
 * somebody and working at a bench are none of the town's business, so they carry
 * no word at all.
 *
 * The tile is the subject's own, so the answer is about the press this prompt is
 * offering — and where a heap and a portable body share one, the answer is the
 * body's, because that is what the verb would lift.
 */
export function tileATakeWouldCite(subject: InteractSubject): TilePos | null {
  if (subject.kind === "item") {
    return subject.at;
  }

  return subject.kind === "npc" && subject.carry === "take" ? subject.at : null;
}

/**
 * The crime word as a prompt carries it: `(theft)`, or `""` for a press that is
 * nobody's business.
 *
 * **The word is the simulation's and the brackets are ours** (the owner's
 * ruling: *"say (steal) (assault) (kidnap)"*). What crosses the wall is a crime
 * id off the blotter's own table and the noun for it
 * (`world/types.ts`'s `crimeWord`), so the day a row is renamed or a sixth one
 * is minted, every prompt in this client says the new word without being told.
 *
 * The **red** is the other half and it is not here: a colour is a fact about a
 * `div` (`hud/panels.ts`'s `InteractPrompt`), and this is the text it colours.
 */
export function crimeTag(word: string): string {
  return word === "" ? "" : `(${word})`;
}
